import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import Modal from '../../components/Modal.jsx';
import { 
  ArrowLeft, 
  Plus, 
  Upload, 
  Play, 
  Edit, 
  Trash2, 
  Clock, 
  Lock, 
  Unlock,
  Loader2,
  BookOpen,
  X
} from 'lucide-react';
import { apiService } from '../../context/apiService.js';
import { toast } from 'react-toastify';

const CourseLessons = () => {
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewLesson, setPreviewLesson] = useState(null);

  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    duration: '',
    order: '',
    isFree: false,
    video: null,
    videoPreview: null
  });

  // Fetch course and lessons data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch course details
      const courseResponse = await apiService.courses.getById(courseId);
      if (courseResponse.data.success) {
        setCourse(courseResponse.data.course);
      }

      // Fetch lessons
      const lessonsResponse = await apiService.lessons.getByCourse(courseId);
      if (lessonsResponse.data.success) {
        setLessons(lessonsResponse.data.lessons);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchData();
    }
  }, [courseId]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLessonForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle video upload
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLessonForm(prev => ({
        ...prev,
        video: file,
        videoPreview: URL.createObjectURL(file)
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setLessonForm({
      title: '',
      description: '',
      duration: '',
      order: lessons.length + 1,
      isFree: false,
      video: null,
      videoPreview: null
    });
    setErrors({});
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!lessonForm.title.trim()) {
      newErrors.title = 'Lesson title is required';
    }
    
    if (!lessonForm.duration.trim()) {
      newErrors.duration = 'Lesson duration is required';
    }

    if (!editingLesson && !lessonForm.video) {
      newErrors.video = 'Video file is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add new lesson
  const handleAddLesson = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLessonLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('title', lessonForm.title);
      formData.append('description', lessonForm.description);
      formData.append('duration', lessonForm.duration);
      formData.append('order', lessonForm.order);
      formData.append('isFree', lessonForm.isFree);
      
      if (lessonForm.video) {
        formData.append('video', lessonForm.video);
      }

      const response = await apiService.lessons.create(courseId, formData);
      
      if (response.data.success) {
        toast.success('Lesson added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchData(); // Refresh lessons list
      } else {
        toast.error(response.data.message || 'Failed to add lesson');
      }
    } catch (error) {
      console.error('Error adding lesson:', error);
      toast.error('Failed to add lesson');
    } finally {
      setLessonLoading(false);
    }
  };

  // Edit lesson
  const handleEditLesson = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLessonLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('title', lessonForm.title);
      formData.append('description', lessonForm.description);
      formData.append('duration', lessonForm.duration);
      formData.append('order', lessonForm.order);
      formData.append('isFree', lessonForm.isFree);
      
      if (lessonForm.video) {
        formData.append('video', lessonForm.video);
      }

      const response = await apiService.lessons.update(editingLesson._id, formData);
      
      if (response.data.success) {
        toast.success('Lesson updated successfully!');
        setShowEditModal(false);
        setEditingLesson(null);
        resetForm();
        fetchData(); // Refresh lessons list
      } else {
        toast.error(response.data.message || 'Failed to update lesson');
      }
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error('Failed to update lesson');
    } finally {
      setLessonLoading(false);
    }
  };

  // Delete lesson
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      const response = await apiService.lessons.delete(lessonId);
      
      if (response.data.success) {
        toast.success('Lesson deleted successfully!');
        fetchData(); // Refresh lessons list
      } else {
        toast.error(response.data.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Failed to delete lesson');
    }
  };

  // Delete course
  const handleDeleteCourse = () => {
    setShowDeleteCourseModal(true);
  };

  const confirmDeleteCourse = async () => {
    try {
      const response = await apiService.courses.delete(courseId);
      
      if (response.data.success) {
        toast.success('Course deleted successfully!');
        navigate('/teacher/courses');
      } else {
        toast.error(response.data.message || 'Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    } finally {
      setShowDeleteCourseModal(false);
    }
  };

  // Open edit modal
  const openEditModal = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      title: lesson.title || '',
      description: lesson.description || '',
      duration: (lesson.duration !== undefined && lesson.duration !== null) ? lesson.duration.toString() : '',
      order: lesson.order || lessons.length + 1,
      isFree: lesson.isFree || false,
      video: null,
      videoPreview: lesson.videoUrl || null
    });
    setShowEditModal(true);
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setLessonForm(prev => ({
      ...prev,
      order: lessons.length + 1
    }));
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading lessons...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/teacher/courses')}
            className="p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">
              {course?.title || 'Course Lessons'}
            </h1>
            <p className="text-secondary-600 mt-1">
              Manage lessons for your course
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={openAddModal}>
            <Plus size={20} className="mr-2" />
            Add Lesson
          </Button>
          <Button 
            onClick={handleDeleteCourse}
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
          >
            <Trash2 size={20} className="mr-2" />
            Delete Course
          </Button>
        </div>
      </div>

      {/* Course Info */}
      {course && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {course.thumbnail && (
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-16 h-12 object-cover rounded-lg"
                />
              )}
              <div>
                <h3 className="font-semibold text-secondary-900">{course.title}</h3>
                <p className="text-sm text-secondary-600">{course.description}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-secondary-600">Total Lessons</p>
              <p className="text-2xl font-bold text-primary-600">{lessons.length}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Lessons List */}
      {lessons.length === 0 ? (
        <Card className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No lessons yet</h3>
          <p className="text-gray-600 mb-4">Start building your course by adding your first lesson.</p>
          <Button onClick={openAddModal}>
            <Plus className="w-4 h-4 mr-2" />
            Add First Lesson
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {lessons.map((lesson, index) => (
            <Card key={lesson._id} className="overflow-hidden hover:shadow-medium transition-shadow">
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-lg">
                        {lesson.order || index + 1}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold text-secondary-900">{lesson.title}</h3>
                      {lesson.isFree ? (
                        <span className="bg-green-100 text-green-700 px-2 py-1 text-xs font-medium rounded-full flex items-center">
                          <Unlock size={12} className="mr-1" />
                          Free
                        </span>
                      ) : (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 text-xs font-medium rounded-full flex items-center">
                          <Lock size={12} className="mr-1" />
                          Premium
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      Uploaded: {lesson.createdAt ? new Date(lesson.createdAt).toLocaleString() : lesson.updatedAt ? new Date(lesson.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                    <p className="text-sm text-secondary-600 mb-2">{lesson.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-secondary-500">
                      <div className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {lesson.duration}
                      </div>
                      {lesson.videoUrl && (
                        <div className="flex items-center">
                          <Play size={14} className="mr-1" />
                          Video Available
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPreviewLesson(lesson)}
                    disabled={!lesson.videoUrl}
                  >
                    <Play size={16} className="mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(lesson)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteLesson(lesson._id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Lesson Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Lesson"
      >
        <form onSubmit={handleAddLesson} className="space-y-4">
          <Input
            label="Lesson Title"
            name="title"
            value={lessonForm.title}
            onChange={handleChange}
            placeholder="Enter lesson title"
            error={errors.title}
            required
          />

          <div>
            <label className="label text-secondary-700 mb-2 block">Description</label>
            <textarea
              name="description"
              value={lessonForm.description}
              onChange={handleChange}
              placeholder="Describe what students will learn in this lesson"
              rows={3}
              className="input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Duration"
              name="duration"
              value={lessonForm.duration}
              onChange={handleChange}
              placeholder="e.g., 10:30"
              error={errors.duration}
              required
            />

            <Input
              label="Order"
              name="order"
              type="number"
              value={lessonForm.order}
              onChange={handleChange}
              placeholder="1"
            />
          </div>

          <div>
            <label className="label text-secondary-700 mb-2 block">Video File</label>
            {lessonForm.videoPreview ? (
              <div className="relative">
                <video
                  src={lessonForm.videoPreview}
                  controls
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setLessonForm(prev => ({ ...prev, video: null, videoPreview: null }))}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
                <p className="text-sm text-secondary-600 mb-2">Upload video file</p>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  className="hidden"
                  id="video-upload"
                />
                <label
                  htmlFor="video-upload"
                  className="btn btn-outline btn-sm cursor-pointer"
                >
                  Choose File
                </label>
              </div>
            )}
            {errors.video && (
              <p className="text-sm text-red-600 mt-1">{errors.video}</p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isFree"
              checked={lessonForm.isFree}
              onChange={handleChange}
              className="mr-2"
            />
            <label className="text-sm text-secondary-700">
              Make this lesson free for preview
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={lessonLoading}
              disabled={lessonLoading}
            >
              {lessonLoading ? 'Adding Lesson...' : 'Add Lesson'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Lesson Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Lesson"
      >
        <form onSubmit={handleEditLesson} className="space-y-4">
          <Input
            label="Lesson Title"
            name="title"
            value={lessonForm.title}
            onChange={handleChange}
            placeholder="Enter lesson title"
            error={errors.title}
            required
          />

          <div>
            <label className="label text-secondary-700 mb-2 block">Description</label>
            <textarea
              name="description"
              value={lessonForm.description}
              onChange={handleChange}
              placeholder="Describe what students will learn in this lesson"
              rows={3}
              className="input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Duration"
              name="duration"
              value={lessonForm.duration}
              onChange={handleChange}
              placeholder="e.g., 10:30"
              error={errors.duration}
              required
            />

            <Input
              label="Order"
              name="order"
              type="number"
              value={lessonForm.order}
              onChange={handleChange}
              placeholder="1"
            />
          </div>

          <div>
            <label className="label text-secondary-700 mb-2 block">Video File</label>
            {lessonForm.videoPreview ? (
              <div className="relative">
                <video
                  src={lessonForm.videoPreview}
                  controls
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setLessonForm(prev => ({ ...prev, video: null, videoPreview: null }))}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
                {editingLesson?.videoUrl && !lessonForm.video && (
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                    Current video
                  </div>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
                <p className="text-sm text-secondary-600 mb-2">Upload new video file (optional)</p>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  className="hidden"
                  id="video-upload-edit"
                />
                <label
                  htmlFor="video-upload-edit"
                  className="btn btn-outline btn-sm cursor-pointer"
                >
                  Choose File
                </label>
              </div>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isFree"
              checked={lessonForm.isFree}
              onChange={handleChange}
              className="mr-2"
            />
            <label className="text-sm text-secondary-700">
              Make this lesson free for preview
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={lessonLoading}
              disabled={lessonLoading}
            >
              {lessonLoading ? 'Updating Lesson...' : 'Update Lesson'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!previewLesson}
        onClose={() => setPreviewLesson(null)}
        title={previewLesson ? `Preview: ${previewLesson.title}` : 'Preview'}
      >
        {previewLesson?.videoUrl ? (
          <video
            src={previewLesson.videoUrl}
            controls
            autoPlay
            className="w-full max-h-[60vh] rounded-lg"
          />
        ) : (
          <p className="text-secondary-500">No video available for this lesson.</p>
        )}
      </Modal>

      {/* Delete Course Confirmation Modal */}
      <Modal
        isOpen={showDeleteCourseModal}
        onClose={() => setShowDeleteCourseModal(false)}
        title="Delete Course"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-secondary-600">
            Are you sure you want to delete this course? This action cannot be undone and will remove:
          </p>
          <ul className="list-disc list-inside text-secondary-600 space-y-1">
            <li>All {lessons.length} lesson(s)</li>
            <li>All lesson videos</li>
            <li>Course thumbnail</li>
            <li>All enrollment data</li>
          </ul>
          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteCourseModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteCourse}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Course
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseLessons;
